/**
 * Shared module key mappings used by both authStorage and accessControl services.
 * Extracted here to avoid circular dependency between the two services.
 *
 * Single source of truth — edit ONLY this file when the backend changes module keys.
 */

// ─────────────────────────────────────────────────────────────────────────────
// BACKEND → FRONTEND MODULE KEY MAPPING
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Maps backend module keys (as returned by the API) to frontend module keys
 * used by route guards and access control checks.
 *
 * Based on actual API response from: GET /api/Auth/login/lean and GET /api/Auth/profile
 */
export const MODULE_KEY_MAPPING: Record<string, string> = {
  // ── 22 New Module Keys (identity mapping — backend and frontend use same keys) ──
  POS: "POS",
  ORDER_MANAGEMENT: "ORDER_MANAGEMENT",
  LOYALTY_PROGRAM: "LOYALTY_PROGRAM",
  AUDIT_LOGS: "AUDIT_LOGS",
  RESERVATION: "RESERVATION",
  TABLE_AND_LOCATION: "TABLE_AND_LOCATION",
  RESTAURANT_PROFILE: "RESTAURANT_PROFILE",
  QR_FLOW: "QR_FLOW",
  CAPTAIN_APP: "CAPTAIN_APP",
  INVENTORY: "INVENTORY",
  KITCHEN_DISPLAY: "KITCHEN_DISPLAY",
  INTELLIGENT_MENU: "INTELLIGENT_MENU",
  BASIC_MENU: "BASIC_MENU",
  WHATSAPP_BUSINESS: "WHATSAPP_BUSINESS",
  ANALYTICS: "ANALYTICS",
  REPORT: "REPORT",
  PROFILE: "PROFILE",
  SETTINGS: "SETTINGS",
  BILLING_HISTORY: "BILLING_HISTORY",
  PROMOTIONAL_CAMPAIGN: "PROMOTIONAL_CAMPAIGN",
  USER_MANAGEMENT: "USER_MANAGEMENT",
  AI_CREDITS: "AI_CREDITS",

  // ── Legacy keys → new canonical keys ─────────────────────────────────────────
  // Old backend keys that may still appear in JWT tokens from older subscriptions
  DASHBOARD: "POS",
  ORDERS: "ORDER_MANAGEMENT",
  KDS: "KITCHEN_DISPLAY",
  TABLES: "TABLE_AND_LOCATION",
  RESERVATIONS: "RESERVATION",
  QR_CODES: "QR_FLOW",
  LOYALTY: "LOYALTY_PROGRAM",
  WHATSAPP: "WHATSAPP_BUSINESS",
  PROMOTIONS: "PROMOTIONAL_CAMPAIGN",
  REPORTS: "REPORT",
  BILLING: "BILLING_HISTORY",
  STAFF: "USER_MANAGEMENT",
  MARKETING: "PROMOTIONAL_CAMPAIGN",
  USERS_MANAGE: "USER_MANAGEMENT",
  AUDIT: "AUDIT_LOGS",

  // ── Legacy module variants ────────────────────────────────────────────────────
  HR: "HR",
  PAYROLL: "PAYROLL",
  INTEGRATIONS: "INTEGRATIONS",
  SUPPLIERS: "INVENTORY",
  PURCHASE_ORDERS: "INVENTORY",
  RECIPES: "INTELLIGENT_MENU",
  WASTAGE: "INVENTORY",
  CUSTOMERS: "LOYALTY_PROGRAM",

  // ── Alternative naming patterns ───────────────────────────────────────────────
  EMPLOYEE: "HR",
  HR_MANAGEMENT: "HR",
  ATTENDANCE: "HR",
  PAYROLL_MANAGEMENT: "PAYROLL",
  MENU: "BASIC_MENU",
  MENU_MANAGEMENT: "BASIC_MENU",
  SMART_MENU: "INTELLIGENT_MENU",
  MENU_AI: "INTELLIGENT_MENU",
  TABLE: "TABLE_AND_LOCATION",
  TABLE_MANAGEMENT: "TABLE_AND_LOCATION",
  BOOKING: "RESERVATION",
  CUSTOMER: "LOYALTY_PROGRAM",
  CLIENT: "LOYALTY_PROGRAM",
  SUPPLIER: "INVENTORY",
  VENDOR: "INVENTORY",
  PURCHASE: "INVENTORY",
  PURCHASE_ORDER: "INVENTORY",
  PO: "INVENTORY",
  RECIPE: "INTELLIGENT_MENU",
  WASTE: "INVENTORY",
  CUSTOMER_LOYALTY: "LOYALTY_PROGRAM",
  MARKETING_CAMPAIGNS: "PROMOTIONAL_CAMPAIGN",
  INTEGRATION: "INTEGRATIONS",
  THIRD_PARTY: "INTEGRATIONS",
  WHATSAPP_INTEGRATION: "WHATSAPP_BUSINESS",
  MESSAGING: "WHATSAPP_BUSINESS",
  SYSTEM_SETTINGS: "SETTINGS",
  CONFIGURATION: "SETTINGS",
  REPORTING: "REPORT",
  BASIC_REPORTS: "REPORT",
  STAFF_MANAGEMENT: "USER_MANAGEMENT",
  EMPLOYEE_MANAGEMENT: "USER_MANAGEMENT",
};

// ─────────────────────────────────────────────────────────────────────────────
// PERMISSION PREFIX → FRONTEND MODULE MAPPING
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Maps individual permission strings (or their first-segment prefix) to the
 * frontend module key they belong to.
 *
 * Used to derive module access from a permissions list when the backend returns
 * modules: [] (e.g. Free Trial plan).
 */
export const PERMISSION_TO_MODULE: Record<string, string> = {
  // POS (module-level)
  POS: "POS",
  POS_OPERATE: "POS",
  POS_DISCOUNT: "POS",
  POS_REFUND: "POS",
  POS_VOID_ITEM: "POS",
  POS_COMPLIMENTARY: "POS",
  POS_PRICE_OVERRIDE: "POS",
  POS_REPRINT_BILL: "POS",
  POS_MANAGER: "POS",

  // Order Management (module-level)
  ORDER_MANAGEMENT: "ORDER_MANAGEMENT",
  ORDERS: "ORDER_MANAGEMENT",
  ORDERS_VIEW: "ORDER_MANAGEMENT",
  ORDERS_MANAGE: "ORDER_MANAGEMENT",

  // Kitchen Display (module-level)
  KITCHEN_DISPLAY: "KITCHEN_DISPLAY",
  KDS: "KITCHEN_DISPLAY",
  KDS_VIEW: "KITCHEN_DISPLAY",

  // Captain App (module-level)
  CAPTAIN_APP: "CAPTAIN_APP",

  // Table and Table Location
  TABLE_FULL_ACCESS: "TABLE_AND_LOCATION",
  TABLE_MANAGE: "TABLE_AND_LOCATION",
  TABLES: "TABLE_AND_LOCATION",
  TABLE_VIEW: "TABLE_AND_LOCATION",
  TABLE_AND_LOCATION: "TABLE_AND_LOCATION",

  // Reservation
  RESERVATION_FULL_ACCESS: "RESERVATION",
  RESERVATIONS_MANAGE: "RESERVATION",
  RESERVATIONS: "RESERVATION",
  RESERVATIONS_VIEW: "RESERVATION",

  // Inventory (new permission strings)
  INVENTORY_DASHBOARD_ACCESS: "INVENTORY",
  INVENTORY_DASHBOARD_VIEW: "INVENTORY",
  OVERWRITE_INGREDIENT_THRESHOLD: "INVENTORY",
  INVENTORY_INVOICE_ACCESS: "INVENTORY",
  INVENTORY_REPORTS_ACCESS: "INVENTORY",
  STOCK_CONSUMPTION_ACCESS: "INVENTORY",
  INVENTORY_STOCK_MANAGE: "INVENTORY",
  PURCHASE_ORDER_ACCESS: "INVENTORY",
  PURCHASE_ORDER_MANAGE: "INVENTORY",
  EDIT_INGREDIENT_ACCESS: "INVENTORY",
  INVENTORY_INGREDIENT_MANAGE: "INVENTORY",
  INVENTORY: "INVENTORY",
  INVENTORY_VIEW: "INVENTORY",
  INVENTORY_MANAGE: "INVENTORY",
  INVENTORY_ADJUST: "INVENTORY",
  INVENTORY_ALERTS: "INVENTORY",
  INVENTORY_CREATE: "INVENTORY",
  INVENTORY_EDIT: "INVENTORY",
  INVENTORY_DELETE: "INVENTORY",
  INVENTORY_TRANSFER: "INVENTORY",

  // Suppliers — sub-feature of INVENTORY
  SUPPLIERS: "INVENTORY",
  SUPPLIER_VIEW: "INVENTORY",
  SUPPLIER_MANAGE: "INVENTORY",
  SUPPLIER_CREATE: "INVENTORY",
  SUPPLIER_DELETE: "INVENTORY",
  SUPPLIER_EDIT: "INVENTORY",

  // Purchase Orders — sub-feature of INVENTORY
  PURCHASE_ORDERS: "INVENTORY",
  PURCHASE_VIEW: "INVENTORY",
  PURCHASE_CREATE: "INVENTORY",
  PURCHASE_APPROVE: "INVENTORY",
  PURCHASE_RECEIVE: "INVENTORY",
  PO_VIEW: "INVENTORY",
  PO_CREATE: "INVENTORY",
  PO_APPROVE: "INVENTORY",
  PO_RECEIVE: "INVENTORY",
  PO_CANCEL: "INVENTORY",

  // Recipes — sub-feature of INTELLIGENT_MENU
  RECIPES: "INTELLIGENT_MENU",
  RECIPE_VIEW: "INTELLIGENT_MENU",
  RECIPE_MANAGE: "INTELLIGENT_MENU",
  RECIPE_CREATE: "INTELLIGENT_MENU",
  RECIPE_DELETE: "INTELLIGENT_MENU",
  RECIPE_EDIT: "INTELLIGENT_MENU",
  RECIPE_COSTING: "INTELLIGENT_MENU",

  // Wastage — sub-feature of INVENTORY
  WASTAGE: "INVENTORY",
  WASTAGE_VIEW: "INVENTORY",
  WASTAGE_LOG: "INVENTORY",
  WASTAGE_MANAGE: "INVENTORY",
  WASTAGE_RECORD: "INVENTORY",
  WASTAGE_APPROVE: "INVENTORY",

  // Customers — sub-feature of LOYALTY_PROGRAM
  CUSTOMERS: "LOYALTY_PROGRAM",
  CUSTOMER_VIEW: "LOYALTY_PROGRAM",
  CUSTOMER_MANAGE: "LOYALTY_PROGRAM",
  CUSTOMER_CREATE: "LOYALTY_PROGRAM",
  CUSTOMER_DELETE: "LOYALTY_PROGRAM",
  CUSTOMER_EDIT: "LOYALTY_PROGRAM",

  // User Management (new)
  USER_MANAGEMENT_FULL_ACCESS: "USER_MANAGEMENT",
  USERS_MANAGE: "USER_MANAGEMENT",
  USER_MANAGEMENT: "USER_MANAGEMENT",
  USERS: "USER_MANAGEMENT",
  STAFF_VIEW: "USER_MANAGEMENT",
  STAFF_MANAGE: "USER_MANAGEMENT",
  USER_VIEW: "USER_MANAGEMENT",
  USER_MANAGE: "USER_MANAGEMENT",
  USER_CREATE: "USER_MANAGEMENT",
  USER_DELETE: "USER_MANAGEMENT",

  // HR
  HR: "HR",
  HR_VIEW: "HR",
  HR_MANAGE: "HR",
  HR_CREATE: "HR",
  HR_EDIT: "HR",
  HR_DELETE: "HR",
  HR_ATTENDANCE_VIEW: "HR",
  HR_ATTENDANCE_MANAGE: "HR",
  HR_SHIFT_MANAGE: "HR",
  HR_LEAVE_MANAGE: "HR",
  ATTENDANCE: "HR",
  ATTENDANCE_VIEW: "HR",
  ATTENDANCE_MANAGE: "HR",
  LEAVE: "HR",
  LEAVE_VIEW: "HR",
  LEAVE_MANAGE: "HR",

  // Payroll
  PAYROLL: "PAYROLL",
  PAYROLL_VIEW: "PAYROLL",
  PAYROLL_MANAGE: "PAYROLL",
  PAYROLL_PROCESS: "PAYROLL",
  PAYROLL_ADJUST: "PAYROLL",
  PAYROLL_APPROVE: "PAYROLL",

  // Loyalty Program (new)
  LOYALTY_MANUAL_ADJUST: "LOYALTY_PROGRAM",
  LOYALTY_CREATE_REWARDS: "LOYALTY_PROGRAM",
  LOYALTY_PROGRAM: "LOYALTY_PROGRAM",
  LOYALTY: "LOYALTY_PROGRAM",
  LOYALTY_VIEW: "LOYALTY_PROGRAM",
  LOYALTY_MANAGE: "LOYALTY_PROGRAM",
  LOYALTY_POINTS: "LOYALTY_PROGRAM",

  // Promotional Campaign (new)
  PROMOTIONAL_CAMPAIGN: "PROMOTIONAL_CAMPAIGN",
  MARKETING: "PROMOTIONAL_CAMPAIGN",
  MARKETING_VIEW: "PROMOTIONAL_CAMPAIGN",
  MARKETING_MANAGE: "PROMOTIONAL_CAMPAIGN",
  MARKETING_CAMPAIGN: "PROMOTIONAL_CAMPAIGN",
  MARKETING_CREATE: "PROMOTIONAL_CAMPAIGN",

  // Reports (new key: REPORT)
  FINANCIAL_REPORTS: "REPORT",
  SALES_REPORTS: "REPORT",
  INVENTORY_REPORTS: "REPORT",
  CUSTOMER_REPORTS: "REPORT",
  REPORTS_FINANCIAL: "REPORT",
  REPORTS_SALES: "REPORT",
  REPORTS_INVENTORY: "REPORT",
  REPORTS_CUSTOMER: "REPORT",
  REPORT: "REPORT",
  REPORTS: "REPORT",
  REPORTS_VIEW: "REPORT",

  // Analytics (new)
  FINANCIAL_ANALYTICS: "ANALYTICS",
  SALES_ANALYTICS: "ANALYTICS",
  INVENTORY_ANALYTICS: "ANALYTICS",
  CUSTOMER_ANALYTICS: "ANALYTICS",
  ANALYTICS_FINANCIAL: "ANALYTICS",
  ANALYTICS_SALES: "ANALYTICS",
  ANALYTICS_INVENTORY: "ANALYTICS",
  ANALYTICS_CUSTOMER: "ANALYTICS",
  ANALYTICS: "ANALYTICS",

  // WhatsApp Business (new)
  WHATSAPP_BUSINESS: "WHATSAPP_BUSINESS",
  WHATSAPP: "WHATSAPP_BUSINESS",
  WHATSAPP_VIEW: "WHATSAPP_BUSINESS",
  WHATSAPP_MANAGE: "WHATSAPP_BUSINESS",
  WHATSAPP_TEMPLATES: "WHATSAPP_BUSINESS",
  WHATSAPP_BILLING: "WHATSAPP_BUSINESS",

  // Menu (new permission strings apply to both Basic and Intelligent menu)
  MENU_FULL_ACCESS: "BASIC_MENU",
  MENU_COST_MANAGEMENT: "INTELLIGENT_MENU",
  BASIC_MENU_MANAGE: "BASIC_MENU",
  INTELLIGENT_MENU_MANAGE: "INTELLIGENT_MENU",
  INTELLIGENT_MENU_COSTING: "INTELLIGENT_MENU",
  INTELLIGENT_MENU: "INTELLIGENT_MENU",
  BASIC_MENU: "BASIC_MENU",
  MENU_VIEW: "BASIC_MENU",
  MENU_MANAGE: "BASIC_MENU",
  MENU_CREATE: "BASIC_MENU",
  MENU_EDIT: "BASIC_MENU",

  // Restaurant Profile (new)
  RESTAURANT_FULL_ACCESS: "RESTAURANT_PROFILE",
  RESTAURANT_PROFILE_MANAGE: "RESTAURANT_PROFILE",
  RESTAURANT_PROFILE: "RESTAURANT_PROFILE",

  // Profile (new)
  PROFILE_FULL_ACCESS: "PROFILE",
  PROFILE: "PROFILE",

  // Settings (new permission strings)
  SETTINGS_FULL_ACCESS: "SETTINGS",
  TAX_CONFIGURATION_SETTINGS: "SETTINGS",
  INVENTORY_SETTINGS: "SETTINGS",
  CUSTOMER_NOTIFICATION_SETTINGS: "SETTINGS",
  ORDER_SETTINGS: "SETTINGS",
  RESERVATION_SETTINGS: "SETTINGS",
  BILLING_SETTINGS: "SETTINGS",
  LOYALTY_PROGRAM_SETTINGS: "SETTINGS",
  SETTINGS: "SETTINGS",
  SETTINGS_VIEW: "SETTINGS",
  SETTINGS_MANAGE: "SETTINGS",
  SETTINGS_GENERAL: "SETTINGS",
  SETTINGS_BILLING: "SETTINGS",
  SETTINGS_TAX: "SETTINGS",
  SETTINGS_PAYMENT: "SETTINGS",

  // Billing History (new)
  BILLING_HISTORY_FULL_ACCESS: "BILLING_HISTORY",
  BILLING_MANAGE: "BILLING_HISTORY",
  BILLING_HISTORY: "BILLING_HISTORY",
  BILLING: "BILLING_HISTORY",

  // AI Credits (new)
  AI_CREDITS: "AI_CREDITS",

  // Integrations
  INTEGRATIONS: "INTEGRATIONS",
  INTEGRATION_VIEW: "INTEGRATIONS",
  INTEGRATION_MANAGE: "INTEGRATIONS",
  INTEGRATIONS_VIEW: "INTEGRATIONS",
  INTEGRATIONS_MANAGE: "INTEGRATIONS",

  // Audit Logs (new)
  AUDIT_LOGS: "AUDIT_LOGS",
  AUDIT: "AUDIT_LOGS",
  AUDIT_VIEW: "AUDIT_LOGS",
  AUDIT_EXPORT: "AUDIT_LOGS",

  // QR Flow (new)
  QR_FLOW: "QR_FLOW",
  QR_CODES: "QR_FLOW",
  GENERATE_QR_CODE: "QR_FLOW",
};

/** Modules that are always accessible regardless of plan or permissions */
export const CORE_MODULES = ["SETTINGS", "PROFILE"];
